import { useState } from 'react'

import type { AtomModalApi } from '@/components/Atoms/AtomModal/useAtomModal'

import { stockImportsApi } from '../../api/stockImportsApi'
import { persistStockImportConfig } from '../../lib/stockImportsConfigPersistence'
import type {
  StockImportConfigModel,
  StockImportConfigRequest,
  StockImportConfigShortModel,
} from '../../types/stockImports.types'

type UseConfigPersistenceParams = {
  config: StockImportConfigModel | null
  isSelfSupplierMode: boolean
  atomModal: AtomModalApi
  onPersisted: (config: StockImportConfigShortModel) => void
}

export const useConfigPersistence = ({
  config,
  isSelfSupplierMode,
  atomModal,
  onPersisted,
}: UseConfigPersistenceParams) => {
  const [savedConfig, setSavedConfig] = useState<StockImportConfigModel | null>(
    null
  )

  const confirmOverwrite = (existing: StockImportConfigShortModel) =>
    atomModal.confirm({
      title: 'У склада уже есть настройка загрузки',
      content: existing.store?.description
        ? `Склад «${existing.store.description}» уже привязан к настройке загрузки остатков. Перезаписать её значениями из формы?`
        : 'Этот склад уже привязан к настройке загрузки остатков. Перезаписать её значениями из формы?',
      okText: 'Перезаписать',
      cancelText: 'Оставить существующую',
      danger: true,
    }).result

  const persist = async (payload: StockImportConfigRequest) => {
    const result = await persistStockImportConfig({
      existingConfig: config ?? savedConfig,
      payload,
      productSupplierId: isSelfSupplierMode
        ? null
        : (payload.productSupplierId ?? null),
      deps: {
        createConfig: stockImportsApi.createConfig,
        updateConfig: stockImportsApi.updateConfig,
        getConfigs: stockImportsApi.getConfigs,
        confirmOverwrite,
      },
    })
    onPersisted(result.config)
    if (result.outcome !== 'adopted-kept') setSavedConfig(result.config)
    return result
  }

  return {
    persistedConfig: config ?? savedConfig,
    savedConfig,
    persist,
  }
}
